<template>
  <div id="finance-calculator">
    <InstalmentAmount
      v-if="instalmentOnly"
      :texts="texts"
      :currency-symbol="currencySymbol"
      :monthly-fee="
        monthlyFee
          | formatAmount(this.decimalSeparator, this.thousandsSeparator)
      "
    ></InstalmentAmount>
    <div v-if="!instalmentOnly">
      <div class="left">
        <div>
          <h2>{{ texts.financeAmountTitle }}</h2>
          <div class="range-wrapper">
            <div class="range-label range-label--value">
              {{ currencySymbol }}
              {{
                financePrice
                  | formatAmount(this.decimalSeparator, this.thousandsSeparator)
              }}
            </div>
            <vue-range-slider
              type="range"
              v-model="financePrice"
              :min="minFinance"
              :max="basePrice"
              :step="100"
              height="10"
              :tooltip="false"
              :bg-style="{ background: '#cccccc' }"
              :process-style="rangeSliderStyle"
            ></vue-range-slider>
            <input
              type="hidden"
              id="finance-price"
              name="finance-price"
              :value="financePrice"
            />
            <span class="range-label range-label--min"
              >{{ currencySymbol }}
              {{
                minFinance
                  | formatAmount(this.decimalSeparator, this.thousandsSeparator)
              }}</span
            >
            <span class="range-label range-label--max"
              >{{ currencySymbol }}
              {{
                basePrice
                  | formatAmount(this.decimalSeparator, this.thousandsSeparator)
              }}</span
            >
          </div>
        </div>
        <div v-if="enableLoanTypeSwitch">
          <p>{{ texts.loanType }}</p>
          <div>
            <span v-for="(item, index) in loanTypes" :key="index">
              <input
                type="radio"
                :id="index"
                name="loan-type"
                :value="item"
                v-model="loanType"
              />
              <label :for="index">{{ item.label }}</label>
            </span>
          </div>
        </div>
        <div>
          <h2>{{ texts.instalmentAmountTitle }}</h2>
          <div class="range-wrapper">
            <div class="range-label range-label--value">
              {{ currencySymbol }}
              {{
                monthlyFee
                  | formatAmount(this.decimalSeparator, this.thousandsSeparator)
              }}
            </div>
            <!-- It is easier to "slide" the duration instead of the price. So we "fake" a price slider that way. -->
            <vue-range-slider
              type="range"
              :reverse="true"
              v-model="duration"
              :min="minDuration"
              :max="maxDuration"
              :step="6"
              height="10"
              :tooltip="false"
              :bg-style="rangeSliderStyle"
              :process-style="{ background: '#cccccc' }"
            ></vue-range-slider>
            <input
              type="hidden"
              id="duration"
              name="duration"
              :value="duration"
            />
            <span class="range-label range-label--min"
              >{{ currencySymbol }}
              {{
                minInstalment
                  | formatAmount(this.decimalSeparator, this.thousandsSeparator)
              }}</span
            >
            <span class="range-label range-label--max"
              >{{ currencySymbol }}
              {{
                maxInstalment
                  | formatAmount(this.decimalSeparator, this.thousandsSeparator)
              }}</span
            >
          </div>
        </div>
      </div>
      <div class="right">
        <div v-if="!noFinance">
          <h2>{{ texts.loanDetailsTitle }}</h2>
          <table class="details">
            <tr>
              <td class="details-label">{{ texts.monthlyFee }}</td>
              <td class="details-value">
                {{ currencySymbol }}
                {{
                  monthlyFee
                    | formatAmount(
                      this.decimalSeparator,
                      this.thousandsSeparator
                    )
                }}
                <input
                  type="hidden"
                  id="monthly-fee"
                  name="monthly-fee"
                  :value="monthlyFee"
                />
              </td>
            </tr>
            <tr>
              <td class="details-label">{{ texts.duration }}</td>
              <td class="details-value">
                {{ duration }}
              </td>
            </tr>
            <tr>
              <td class="details-label">{{ texts.apr }}</td>
              <td class="details-value">
                {{
                  apr
                    | formatPercentage
                    | formatAmount(
                      this.decimalSeparator,
                      this.thousandsSeparator
                    )
                }}%
              </td>
            </tr>
            <tr>
              <td class="details-label">
                {{ texts.totalDue }}
              </td>
              <td class="details-value">
                {{ currencySymbol }}
                {{
                  totalDue
                    | formatAmount(
                      this.decimalSeparator,
                      this.thousandsSeparator
                    )
                }}
              </td>
            </tr>
          </table>
        </div>
        <p v-if="noFinance" class="error">{{ texts.noFinance }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import InstalmentAmount from "./components/InstalmentAmount";
import VueRangeSlider from "vue-range-component";

export default {
  name: "FinanceCalculator",
  components: { InstalmentAmount, VueRangeSlider },
  props: {
    basePrice: { type: Number },
    configUrl: { type: String },
    instalmentOnly: { type: Boolean }
  },
  data: function() {
    return {
      texts: {},
      currencySymbol: "$",
      thousandsSeparator: ",",
      decimalSeparator: ".",
      enableLoanTypeSwitch: true,
      loanTypes: [],
      loanType: {},
      duration: 84,
      financePrice: 0,
      googleFontFamily: "Open Sans",
      googleFontWeights: "300,400,600,700,800",
      rangeColor: "#cccccc"
    };
  },
  computed: {
    rangeSliderStyle: function() {
      return {
        background: this.rangeColor
      };
    },
    noFinance: function() {
      // Finance amount too low.
      if (this.financePrice < this.loanType.minFinance) return true;
      // Finance amount too high.
      if (this.financePrice > this.loanType.maxFinance) return true;
      // Finance percentage too low.
      return (
        this.financePrice < this.basePrice * this.loanType.minFinancePortion
      );
    },
    minFinance: function() {
      return Math.max(
        this.loanType.minFinance,
        this.basePrice * this.loanType.minFinancePortion
      );
    },
    monthlyFee: function() {
      return this.calculateInstalment(this.duration);
    },
    maxInstalment: function() {
      return this.calculateInstalment(this.minDuration);
    },
    minInstalment: function() {
      return this.calculateInstalment(this.maxDuration);
    },
    totalDue: function() {
      return this.duration * this.monthlyFee;
    },
    apr: function() {
      return this.loanType.apr;
    },
    durationOptions: function() {
      let max = 84;
      if (this.financePrice <= 15000) max = 60;
      if (this.financePrice <= 10000) max = 48;
      if (this.financePrice <= 7500) max = 42;
      if (this.financePrice <= 5600) max = 36;
      if (this.financePrice <= 3700) max = 30;
      if (this.financePrice <= 2500) max = 24;

      let durations = [];

      for (let i in this.loanType.durations) {
        if (this.loanType.durations.hasOwnProperty(i)) {
          let duration = this.loanType.durations[i];
          if (duration <= max) {
            durations.push(duration);
          }
        }
      }

      return durations;
    },
    maxDuration: function() {
      const [maxDuration] = this.durationOptions.slice(-1);
      return maxDuration;
    },
    minDuration: function() {
      const [minDuration] = this.durationOptions.slice(0);
      return minDuration;
    }
  },
  // We need to wait for the data before we start rendering the component.
  async beforeCreate() {
    // @todo Proper error handling.
    const data = await window
      // Props are not processed yet so we need to access the raw propsData.
      .fetch(this.$options.propsData.configUrl)
      .then(response => response.json());

    for (let param in data) {
      if (data.hasOwnProperty(param)) {
        this[param] = data[param];
      }
    }

    // Initial finance price.
    this.financePrice = this.basePrice;

    // Initial loan type.
    this.loanType = this.loanTypes[0];

    // Initial duration should be the maximum possible for current configuration.
    this.duration = this.maxDuration;

    // Load the appropriate Google Font.
    const linkNode = document.createElement("link");
    linkNode.type = "text/css";
    linkNode.rel = "stylesheet";
    linkNode.href =
      "//fonts.googleapis.com/css?family=" +
      encodeURIComponent(this.googleFontFamily) +
      ":" +
      this.googleFontWeights +
      "";
    document.head.appendChild(linkNode);
  },
  updated() {
    const components = document.querySelectorAll("finance-calculator");

    for (let component of components) {
      // TODO: Exclude components that run in "installment only" mode.
      // Publish all inputs and selects to the world by populating hidden inputs outside of the Shadow DOM.
      const inputs = this.$el.querySelectorAll("input, select");
      for (let source of inputs) {
        let input = component.parentNode.querySelector(
          "input.finance-calculator-" + source.name
        );
        if (!input) {
          input = component.ownerDocument.createElement("input");
          input.type = "hidden";
          input.name = source.name;
          input.classList.add("finance-calculator-" + source.name);
          component.parentNode.appendChild(input);
        }
        if (source.name === "loan-type") {
          // We need to get the actual value to pass from the Vue component.
          input.value = this.loanType.name;
        } else {
          input.value = source.value;
        }
      }
    }

    // Set the font family for the component.
    let style = this.$el.getAttribute("style");
    style += ";font-family:" + this.googleFontFamily + ";";
    this.$el.setAttribute("style", style);
  },
  filters: {
    formatAmount: function(value, decimalSeparator, thousandsSeparator) {
      let amount = parseFloat(value).toFixed(2);

      return amount
        .replace(".", decimalSeparator)
        .replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    },
    formatPercentage: function(value) {
      return value * 100;
    }
  },
  methods: {
    calculateInstalment: function(duration) {
      return this.PMT(
        Math.pow(1 + this.apr, 1 / 12) - 1,
        duration,
        -1 * this.financePrice
      );
    },
    PMT: function(rate, nper, pv, fv, type) {
      if (!fv) fv = 0;
      if (!type) type = 0;

      if (rate === 0) return -(pv + fv) / nper;

      let pvif = Math.pow(1 + rate, nper);
      let pmt = (rate / (pvif - 1)) * -(pv * pvif + fv);

      if (type === 1) {
        pmt /= 1 + rate;
      }

      return pmt;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../node_modules/vue-range-component/dist/vue-range-slider.css";

#finance-calculator {
  font-family: "HelveticaNeue-Light", "Helvetica Neue Light", "Helvetica Neue",
    Helvetica, Arial, "Lucida Grande", sans-serif;
  font-weight: 300;

  h2 {
    font-size: medium;
    margin-top: 40px;
  }

  .details {
    border-spacing: 10px;

    .details-label {
      background: #eeeeee;
      padding: 10px 40px 10px 20px;
    }

    .details-value {
      font-weight: bold;
    }
  }

  .range-wrapper {
    display: block;
    width: 300px;

    .range-label {
      &--max {
        float: right;
      }

      &--value {
        font-weight: bold;
        margin-bottom: 10px;
        text-align: center;
      }
    }
  }

  .left {
    float: left;
  }

  .right {
    float: right;
  }
}
</style>
